import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FirestoreService } from '../firestore/firestore.service';
import * as moment from 'dayjs';

/*
  Generated class for the updateJobPosts provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable({providedIn: 'root'})
export class BusinessService {

  constructor(
    private fp: FirestoreService,
  ) {
  }

  public updateJobPosts(jobPost) {
    jobPost.personemail = jobPost.personemail.trim().toLowerCase();
    return this.fp.doc(`jobboard/${jobPost.jobpostId}`).ref.set(jobPost);
  }

  public createEmployeeCompany(data) {
    // console.log("data", data);
    return this.fp.post<any>('/invoices/create/companyemployeeinvoice', data);
  }

  public getEmployeeCompanyTax(data) {
    // console.log("data", data);
    return this.fp.post('/invoices/get/companyemployeetax', data);
  }

  public createInvoice(data) {
    return this.fp.post('/invoices/create/invoice', data);
  }

  public issueCoupon(data) {
    // console.log(nucampInvoiceId);
    return this.fp.post('/invoices/issuecoupon', data);
  }

  public markAsVoid(nucampInvoiceId, stripeInvoiceId) {
    console.log(nucampInvoiceId, stripeInvoiceId);
    return this.fp.post('/invoices/void', {nucampInvoiceId, stripeInvoiceId});
  }

  public discountInvoice(nucampInvoiceId, stripeInvoiceId, amount) {
    return this.fp.post('/invoices/discount', {nucampInvoiceId, stripeInvoiceId, amount, roles: ['admin', 'invoicemanager']});
  }

  public updateInvoiceBeneficiaries(data) {
    return this.fp.post('/invoices/update/beneficiaries', data);
  }

  public getCompanyById(companyId) {
    return this.getCompanies([companyId]).pipe(map(companies => {
      console.log("obs companies", companies);
      return companies[0];
    }))
  }

  public updateCompanyNoteSessions(company, data) {
    return this.fp.doc<any>(`companyext/${company.id}`).ref
      .get()
      .then(doc => {
        if (doc.exists) {
          return this.fp.doc<any>(`companyext/${company.id}`).ref.update({notesessions: data});
        } else {
          return this.fp.doc<any>(`companyext/${company.id}`).ref.set({id: company.id, notesessions: data}, {merge: true});
        }
      });
  }

  public getCompanySessions(companyId) {
    return this.fp.docWithIds$<any>(`companyext/${companyId}`)
  }

  public getInvoices() {
    const invoices$: Observable<any[]> = this.fp.colWithIds$('invoice');
    const employees$: Observable<any[]> = this.fp.colWithIds$('employee');
    const companies$: Observable<any[]> = this.fp.colWithIds$('company');

    return combineLatest([invoices$, employees$, companies$]).pipe(map(([invoices, employees, companies]) => {
      return invoices.map(invoice => {
        invoice.employee = null;
        invoice.company = null;
        const employee = employees.find(emp => emp.invoices.includes(invoice.id));
        if (employee) {
          invoice.employee = employee;
          invoice.company = companies.find(comp => comp.id === employee.companyId);
        }
        return invoice
      })
    }))
  }

  public getCompanies(companyIds?) {
    let companies$: Observable<any[]>;

    if (!companyIds) {
      companies$ = combineLatest([
        this.fp.colWithIds$<any>('company'),
        this.fp.colWithIds$<any>('companyext'),
      ])
      .pipe(map(([companies, ext]) => {
        console.log(companies, ext)
        return companies.map(company => {
          const companyext = ext.find(c => c.id === company.id);
          if (companyext) return {...company, ...companyext};
          else return company;
        })
      }))
    } else {
      companies$ = combineLatest([
          this.fp.colWithIds$<any>('company', ref => ref.where("id", "in", companyIds)),
          this.fp.colWithIds$<any>('companyext', ref => ref.where("id", "in", companyIds)),
        ])
        .pipe(map(([companies, ext]) => {
          console.log(companies, ext)
          return companies.map(company => {
            const companyext = ext.find(c => c.id === company.id);
            if (companyext) return {...company, ...companyext};
            else return company;
          })
        }))
    }

    return companies$.pipe(
      switchMap(companies => {
        return combineLatest(companies.map(company => this.fp.colWithIds$('employee', ref => ref.where("companyId", "==", company.id))
          .pipe(
            switchMap((employees: any[]) => {
              return combineLatest(employees.map(employee => {
                if (employee.invoices && employee.invoices.length > 0) {
                  return combineLatest<any[]>(employee.invoices.map((invoiceid) => this.fp.docWithIds$(`invoice/${invoiceid}`)));
                }
                else return of (null);
              })).pipe(map(invoices => ({...company, employees, invoices: invoices.filter(i => i).reduce((acc, cur) => [...acc, ...cur], [])})))
            })
          )
        ))
      }),
    )
  }

  public getEmployeeById(employeeId) {
    return this.fp.doc$(`employee/${employeeId}`);
  }

  public getEmployeeInvoicesByEmail(email) {
    return this.fp.colWithIds$('employee', ref => ref.where('email', '==', email.toLowerCase().trim()))
      .pipe(
        switchMap(result => {
          if (result && result.length > 0) {
            let invoices = of ([]);
            let company = of ();
            const employee:any = result[0];
            if (employee.invoices && employee.invoices.length > 0) {
              invoices = combineLatest<any[]>(employee.invoices.map(invoice => this.fp.docWithIds$(`invoice/${invoice}`)));
            }
            if (employee.companyId) {
              company = this.fp.docWithIds$(`company/${employee.companyId}`);
            }

            return combineLatest([invoices, company]).pipe(map(([invs, comp])=> ({...employee, invoices: invs, company: comp})));
          } else {
            return of (null);
          }
        })
      );
  }

  public getJobPostsByEmail(email) {
    return this.fp.colWithIds$('jobboard', ref => ref.where('personemail', '==', email))
      .pipe((map((result:any) => {
        return result.sort((a, b) => {
          if (a.dateposted && b.dateposted && moment(a.dateposted).isAfter(moment(b.dateposted))) return -1;
          else return 1;
        })
      })));
  }

  public getInvoice(invoiceId) {
    return this.fp.doc(`invoice/${invoiceId}`).ref.get().then(doc => doc.data());
  }

  public deleteJobpost(jobPost) {
    return this.fp.doc(`jobboard/${jobPost.jobpostId}`).ref.delete();
  }

  public getJobs() {
    return this.fp.colWithIds$('jobboard');
  }
}
